import {FormControl, FormHelperText, InputLabel, MenuItem, MenuItemProps, Select, SelectProps} from '@mui/material';
import {nanoid} from 'nanoid/non-secure';
import {Controller, useFormContext, useFormState} from 'react-hook-form';
import {ComponentWithRequiredChildElements, PropsWithRequiredChildElements} from '../react/types';

type AllowedSelectProps = Omit<SelectProps, 'labelId' | 'variant'> & Partial<Pick<SelectProps, 'variant'>>;
export interface SelectInputFieldProps extends PropsWithRequiredChildElements<AllowedSelectProps, MenuItemProps> {
  label: string;
  minWidth?: number | string;
  name: string;
}

export const SelectInputField: ComponentWithRequiredChildElements<SelectInputFieldProps> = ({
  children,
  defaultValue = '',
  label,
  minWidth,
  name,
  onBlur,
  onChange,
  required,
  sx,
  variant = 'outlined',
  ...selectProps
}: SelectInputFieldProps) => {
  const {control} = useFormContext();
  const {errors} = useFormState();
  const error = errors[name]?.message as string | undefined;
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({field}) => {
        const labelId = nanoid();
        const helperId = nanoid();
        return (
          <FormControl fullWidth={minWidth === undefined} sx={{...sx, minWidth}}>
            <InputLabel error={!!error} id={labelId} required={required}>
              {label}
            </InputLabel>
            <Select
              {...selectProps}
              {...field}
              aria-describedby={error && helperId}
              label={label}
              labelId={labelId}
              onBlur={(event) => {
                field.onBlur();
                if (onBlur) {
                  onBlur(event);
                }
              }}
              onChange={(event, child) => {
                field.onChange(event);
                if (onChange) {
                  onChange(event, child);
                }
              }}
              variant={variant}
            >
              {Array.from([children])
                .flat()
                .filter((child) => child.type === MenuItem)}
            </Select>
            {error && (
              <FormHelperText error id={helperId}>
                {error}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};
